import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText, Plus, Edit, Trash2, Copy, Power,
  X, CheckCircle2, Clock, BarChart3, Layout, Download, Eye, Globe,
} from 'lucide-react';
import apiClient from '../../utils/apiClient';
import CommonToaster from '../Common/CommonToaster';
import { PageHeader, AppButton, SearchInput, EmptyState, ConfirmModal, StatsCard, StatsGrid, Tabs, TabsList, TabsTrigger } from '../ui';
import { getCurrentUser } from '../../utils/auth';
import { can } from '../../utils/access';
import { getBaseUrl } from '../../utils/getBaseUrl';
import { baseHost } from '../../utils/brandHost';
import { CMS_PLACEMENTS } from './cmsPlacements';
import './enquiry-forms.scss';

const placementLabel = (value) => CMS_PLACEMENTS.find((p) => p.value === value)?.short || value;

const EnquiryFormsList = () => {
  const navigate = useNavigate();
  const canManage = can('enquiryForms.manage');
  const [forms, setForms] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('forms'); // 'forms' | 'templates'
  const [deleteId, setDeleteId] = useState(null);
  const [embedForm, setEmbedForm] = useState(null);
  const [embedMode, setEmbedMode] = useState('inline'); // 'inline' | 'widget'
  const [widgetConfig, setWidgetConfig] = useState({
    position: 'bottom-right',
    buttonText: 'Enquire Now',
    buttonColor: '#3b82f6',
  });
  const [usingTemplate, setUsingTemplate] = useState(null);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  const fetchForms = useCallback(async () => {
    setLoading(true);
    try {
      const [formsRes, templatesRes, coursesRes] = await Promise.all([
        apiClient.get('/enquiry-forms'),
        apiClient.get('/enquiry-forms/templates'),
        apiClient.get('/courses/names'),
      ]);
      setForms(formsRes.data.forms || []);
      setTemplates(templatesRes.data.templates || []);
      setCourses(coursesRes.data.courses || []);
    } catch {
      setToast({ show: true, message: 'Failed to load forms.', type: 'error' });
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchForms(); }, [fetchForms]);

  // ─── Stats ─────────────────────────────────────────────────────────────
  const totalForms = forms.length;
  const activeForms = forms.filter(f => f.status === 'active').length;
  const totalSubmissions = forms.reduce((s, f) => s + (f.submissionCount || 0), 0);

  // ─── Search filter ─────────────────────────────────────────────────────
  const filtered = forms.filter(f =>
    f.name.toLowerCase().includes(search.toLowerCase())
  );

  // ─── Delete ────────────────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await apiClient.delete(`/enquiry-forms/${deleteId}`);
      setToast({ show: true, message: 'Form deleted.', type: 'success' });
      setDeleteId(null);
      fetchForms();
    } catch {
      setToast({ show: true, message: 'Failed to delete form.', type: 'error' });
    }
  };

  // ─── Toggle status ─────────────────────────────────────────────────────
  const handleToggle = async (id) => {
    try {
      const { data } = await apiClient.patch(`/enquiry-forms/${id}/toggle`);
      setToast({ show: true, message: data.message, type: 'success' });
      fetchForms();
    } catch {
      setToast({ show: true, message: 'Failed to toggle form status.', type: 'error' });
    }
  };

  // ─── Embed script ──────────────────────────────────────────────────────
  const getEmbedScript = (form, forMode) => {
    const user = getCurrentUser();
    const institute = user?.institute || {};
    const subdomain = institute.subdomain || '';
    const customDomain = institute.customDomain || '';
    const apiKey = institute.publicApi?.apiKey || 'YOUR_API_KEY';

    // Embed script is served by the backend at /embed/form.js
    // Use custom domain > subdomain > backend API origin as fallback
    let embedHost;
    if (customDomain) {
      embedHost = `https://${customDomain}`;
    } else if (subdomain) {
      embedHost = `https://${baseHost(subdomain)}`;
    } else {
      // Derive from API base URL (works for localhost:5300 too)
      const apiBase = getBaseUrl().replace(/\/api$/, '');
      embedHost = apiBase;
    }

    const m = forMode || embedMode;
    let tag = `<script src="${embedHost}/embed/form.js" data-form-id="${form._id}" data-api-key="${apiKey}"`;
    if (m === 'widget') {
      tag += ` data-mode="widget" data-position="${widgetConfig.position}" data-button-text="${widgetConfig.buttonText}" data-button-color="${widgetConfig.buttonColor}"`;
    }
    tag += `></script>`;
    return tag;
  };

  const copyEmbedScript = (form, forMode) => {
    const script = getEmbedScript(form, forMode);
    navigator.clipboard.writeText(script).then(() => {
      setToast({ show: true, message: 'Embed script copied!', type: 'success' });
    });
  };

  // ─── Use template ──────────────────────────────────────────────────────
  const handleUseTemplate = async (templateId) => {
    setUsingTemplate(templateId);
    try {
      await apiClient.post(`/enquiry-forms/use-template/${templateId}`);
      setToast({ show: true, message: 'Form created from template!', type: 'success' });
      setActiveTab('forms');
      fetchForms();
    } catch {
      setToast({ show: true, message: 'Failed to create form from template.', type: 'error' });
    }
    setUsingTemplate(null);
  };

  // ─── Template search filter ────────────────────────────────────────────
  const filteredTemplates = templates.filter(t =>
    t.name.toLowerCase().includes(search.toLowerCase()) ||
    (t.templateCategory || '').toLowerCase().includes(search.toLowerCase())
  );

  // ─── Tab switch ────────────────────────────────────────────────────────
  const switchTab = (tab) => {
    setSearch('');
    setActiveTab(tab);
  };

  return (
    <div className="ef-page">
      <CommonToaster show={toast.show} message={toast.message} type={toast.type} onClose={() => setToast((t) => ({ ...t, show: false }))} />

      <PageHeader
        icon={FileText}
        title="Enquiry Forms"
        description="Create and manage custom enquiry forms for your website"
      />

      {/* Tabs + Create Form (same row) */}
      <div className="ef-tabs-row">
        <Tabs value={activeTab} onChange={switchTab} className="ef-tabs-inline">
          <TabsList>
            <TabsTrigger value="forms">
              <FileText size={15} /> My Forms ({forms.length})
            </TabsTrigger>
            {templates.length > 0 && (
              <TabsTrigger value="templates">
                <Layout size={15} /> Templates ({templates.length})
              </TabsTrigger>
            )}
          </TabsList>
        </Tabs>
        {canManage && (
          <AppButton onClick={() => navigate('/enquiry-forms/new')}>
            <Plus size={16} /> Create Form
          </AppButton>
        )}
      </div>

      {activeTab === 'forms' && (
        <>
      {/* Stats */}
      <StatsGrid cols={3} className="ef-stats-row">
        <StatsCard title="Total Forms" value={totalForms} icon={FileText} iconColor="blue" />
        <StatsCard title="Active" value={activeForms} icon={CheckCircle2} iconColor="emerald" />
        <StatsCard title="Total Submissions" value={totalSubmissions} icon={BarChart3} iconColor="violet" />
      </StatsGrid>

      {/* Toolbar */}
      <div className="ef-toolbar">
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Search forms..."
        />
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-center py-5">Loading...</div>
      ) : filtered.length === 0 ? (
        <>
          <EmptyState
            icon={<FileText size={48} />}
            title="No enquiry forms yet"
            description="Create your first form to start capturing leads from your website."
            action={
              <div className="d-flex flex-column align-items-center gap-2">
                {canManage && (
                  <AppButton onClick={() => navigate('/enquiry-forms/new')}>
                    <Plus size={16} /> Create Form
                  </AppButton>
                )}
                {templates.length > 0 && (
                  <button className="ef-browse-templates-link" onClick={() => switchTab('templates')}>
                    <Layout size={14} /> Or browse {templates.length} ready-made template{templates.length !== 1 ? 's' : ''}
                  </button>
                )}
              </div>
            }
          />
        </>
      ) : (
        <div className="ef-table-wrap">
          <table className="ef-table">
            <thead>
              <tr>
                <th>Form</th>
                <th>Status</th>
                <th>Fields</th>
                <th>Submissions</th>
                <th>Last Submission</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((form) => (
                <tr key={form._id}>
                  <td>
                    <div className="ef-form-name">{form.name}</div>
                    {form.description && <div className="ef-form-desc">{form.description}</div>}
                    {form.cmsPlacements?.length > 0 && (
                      <span
                        className={`ef-cms-badge ${form.status === 'active' ? '' : 'is-paused'}`}
                        title={form.status === 'active'
                          ? 'Live on your CMS website'
                          : 'Inactive — your website shows its default form until you activate this one'}
                      >
                        <Globe size={11} /> Website · {form.cmsPlacements.map(placementLabel).join(' + ')}
                      </span>
                    )}
                  </td>
                  <td>
                    <span className={`ef-status-badge ${form.status}`}>
                      {form.status === 'active' ? <CheckCircle2 size={12} /> : <X size={12} />}
                      {form.status}
                    </span>
                  </td>
                  <td>{form.fields?.length || 0}</td>
                  <td>{form.submissionCount || 0}</td>
                  <td>
                    {form.lastSubmissionAt ? (
                      <span className="d-flex align-items-center gap-1">
                        <Clock size={12} />
                        {new Date(form.lastSubmissionAt).toLocaleDateString()}
                      </span>
                    ) : '—'}
                  </td>
                  <td>{new Date(form.createdAt).toLocaleDateString()}</td>
                  <td>
                    <div className="ef-actions">
                      {canManage && (
                        <button onClick={() => navigate(`/enquiry-forms/edit/${form._id}`)} title="Edit">
                          <Edit size={14} />
                        </button>
                      )}
                      <button onClick={() => setEmbedForm(form)} title="Get embed script">
                        <Copy size={14} />
                      </button>
                      {canManage && (
                        <>
                          <button onClick={() => handleToggle(form._id)} title={form.status === 'active' ? 'Deactivate' : 'Activate'}>
                            <Power size={14} />
                          </button>
                          <button className="ef-action-delete" onClick={() => setDeleteId(form._id)} title="Delete">
                            <Trash2 size={14} />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
        </>
      )}

      {/* Templates Tab */}
      {activeTab === 'templates' && (
        <div className="ef-templates-section">
          <div className="ef-toolbar">
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder="Search templates..."
            />
          </div>

          {loading ? (
            <div className="text-center py-5">Loading...</div>
          ) : filteredTemplates.length === 0 ? (
            <EmptyState
              icon={<Layout size={48} />}
              title="No templates available"
              description="Templates created by the platform admin will appear here."
            />
          ) : (
            <div className="ef-templates-grid">
              {filteredTemplates.map(tpl => (
                <div key={tpl._id} className="ef-template-card">
                  <div className="ef-template-card__header">
                    <h4>{tpl.name}</h4>
                    {tpl.templateCategory && (
                      <span className="ef-template-card__category">{tpl.templateCategory}</span>
                    )}
                  </div>
                  {tpl.description && <p className="ef-template-card__desc">{tpl.description}</p>}
                  <div className="ef-template-card__meta">
                    <span>{tpl.fields?.length || 0} fields</span>
                    <span>Theme: {tpl.settings?.theme || 'default'}</span>
                  </div>

                  {/* Inline preview */}
                  <div className="ef-template-card__preview">
                    {tpl.fields?.map((field, fi) => (
                      <div key={fi} className="ef-template-card__preview-field">
                        <label>
                          {field.label}
                          {field.required && <span className="ef-template-card__required">*</span>}
                        </label>
                        {field.type === 'textarea' ? (
                          <textarea placeholder={field.placeholder} />
                        ) : field.type === 'dropdown' ? (
                          <select>
                            <option>{field.placeholder || 'Select...'}</option>
                            {field.defaultField === 'course'
                              ? courses.map((c, ci) => <option key={ci}>{c.name}</option>)
                              : field.options?.map((o, oi) => <option key={oi}>{o}</option>)
                            }
                          </select>
                        ) : (
                          <input type="text" placeholder={field.placeholder} />
                        )}
                      </div>
                    ))}
                    <div className="ef-template-card__preview-btn" >
                      {tpl.settings?.submitButtonText || 'Submit'}
                    </div>
                  </div>

                  <div className="ef-template-card__actions">
                    <button className="ef-template-card__preview-link" onClick={() => setPreviewTemplate(tpl)}>
                      <Eye size={14} /> Full Preview
                    </button>
                    {canManage && (
                      <AppButton
                        size="sm"
                        className="ef-template-card__use-btn"
                        onClick={() => handleUseTemplate(tpl._id)}
                        disabled={usingTemplate === tpl._id}
                      >
                        <Download size={14} /> {usingTemplate === tpl._id ? 'Creating...' : 'Use Template'}
                      </AppButton>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteId && (
        <ConfirmModal
          show={!!deleteId}
          title="Delete Form"
          message={`Are you sure you want to delete this form? This cannot be undone. Any embed scripts using this form will stop working.${
            forms.find((f) => f._id === deleteId)?.cmsPlacements?.length
              ? ' Your CMS website will go back to its default form.'
              : ''
          }`}
          onConfirm={handleDelete}
          onHide={() => setDeleteId(null)}
          confirmText="Delete"
          variant="danger"
        />
      )}

      {/* Template Preview Modal */}
      {previewTemplate && (
        <div className="ef-embed-modal-overlay" onClick={() => setPreviewTemplate(null)}>
          <div className="ef-embed-modal ef-preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ef-embed-modal-header">
              <h3>{previewTemplate.name}</h3>
              <button onClick={() => setPreviewTemplate(null)}><X size={20} /></button>
            </div>
            {previewTemplate.description && (
              <p className="ef-preview-modal__desc">{previewTemplate.description}</p>
            )}
            <div className="ef-preview-modal__form">
              {previewTemplate.fields?.map((field, fi) => (
                <div key={fi} className="ef-preview-modal__field">
                  <label>
                    {field.label}
                    {field.required && <span className="ef-template-card__required">*</span>}
                  </label>
                  {field.type === 'textarea' ? (
                    <textarea placeholder={field.placeholder} />
                  ) : field.type === 'dropdown' ? (
                    <select>
                      <option>{field.placeholder || 'Select...'}</option>
                      {field.defaultField === 'course'
                        ? courses.map((c, ci) => <option key={ci}>{c.name}</option>)
                        : field.options?.map((o, oi) => <option key={oi}>{o}</option>)}
                    </select>
                  ) : field.type === 'radio' ? (
                    <div className="ef-preview-modal__options">
                      {field.options?.map((o, oi) => (
                        <label key={oi}><input type="radio" name={`preview-radio-${fi}`} /> {o}</label>
                      ))}
                    </div>
                  ) : field.type === 'checkbox' ? (
                    <div className="ef-preview-modal__options">
                      {field.options?.map((o, oi) => (
                        <label key={oi}><input type="checkbox" /> {o}</label>
                      ))}
                    </div>
                  ) : (
                    <input type={field.type === 'email' ? 'email' : 'text'} placeholder={field.placeholder} />
                  )}
                </div>
              ))}
              <div className="ef-preview-modal__submit-btn">
                {previewTemplate.settings?.submitButtonText || 'Submit'}
              </div>
            </div>
            <div className="ef-preview-modal__footer">
              <span className="ef-preview-modal__meta">
                {previewTemplate.fields?.length || 0} fields &middot; Theme: {previewTemplate.settings?.theme || 'default'}
                {previewTemplate.templateCategory && <> &middot; {previewTemplate.templateCategory}</>}
              </span>
              {canManage && (
                <AppButton
                  size="sm"
                  onClick={() => { setPreviewTemplate(null); handleUseTemplate(previewTemplate._id); }}
                  disabled={usingTemplate === previewTemplate._id}
                >
                  <Download size={14} /> {usingTemplate === previewTemplate._id ? 'Creating...' : 'Use This Template'}
                </AppButton>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Embed Script Modal */}
      {embedForm && (
        <div className="ef-embed-modal-overlay" onClick={() => setEmbedForm(null)}>
          <div className="ef-embed-modal ef-embed-modal--wide" onClick={(e) => e.stopPropagation()}>
            <div className="ef-embed-modal-header">
              <h3>Add Form to Your Website — {embedForm.name}</h3>
              <button onClick={() => setEmbedForm(null)}><X size={20} /></button>
            </div>

            {/* Embed type tabs */}
            <div className="ef-embed-tabs">
              <button
                className={`ef-embed-tab ${embedMode === 'inline' ? 'ef-embed-tab--active' : ''}`}
                onClick={() => setEmbedMode('inline')}
              >
                <Layout size={16} /> Inline Embed
              </button>
              <button
                className={`ef-embed-tab ${embedMode === 'widget' ? 'ef-embed-tab--active' : ''}`}
                onClick={() => setEmbedMode('widget')}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                {' '}Floating Widget
              </button>
            </div>

            {embedMode === 'inline' ? (
              /* ─── Inline Embed ──────────────────────────────────────── */
              <div className="ef-embed-steps">
                <div className="ef-embed-step">
                  <span className="ef-embed-step__num">1</span>
                  <div className="ef-embed-step__content">
                    <strong>Copy the embed script</strong>
                    <p>Click the button below to copy the script to your clipboard.</p>
                  </div>
                </div>

                <div className="ef-embed-code">
                  {getEmbedScript(embedForm, 'inline')}
                </div>

                <AppButton className="ef-embed-copy-btn" onClick={() => copyEmbedScript(embedForm, 'inline')}>
                  <Copy size={16} /> Copy Script
                </AppButton>

                <div className="ef-embed-step">
                  <span className="ef-embed-step__num">2</span>
                  <div className="ef-embed-step__content">
                    <strong>Paste it where you want the form to appear</strong>
                    <p>The form will render <strong>exactly where you place the script</strong>. Open your website's HTML editor and paste it in the desired location — such as a "Contact Us" section, sidebar, or landing page.</p>
                  </div>
                </div>

                <div className="ef-embed-code ef-embed-code--example">
{`<!-- Example: Contact page -->
<section>
  <h2>Enquire Now</h2>
  <p>Fill out the form below and we'll get back to you.</p>

  <!-- The form will appear right here ↓ -->
  ${getEmbedScript(embedForm, 'inline')}
</section>`}
                </div>

                <div className="ef-embed-note ef-embed-note--info">
                  <strong>How it works:</strong> The script automatically creates the form right below itself. No extra <code>&lt;div&gt;</code> or container needed — just paste the script and the form shows up there.
                </div>

                <div className="ef-embed-step">
                  <span className="ef-embed-step__num">3</span>
                  <div className="ef-embed-step__content">
                    <strong>Whitelist your domain</strong>
                    <p>Go to <strong>Settings → Public API</strong> and add your website's domain (e.g. <code>www.yourinstitute.com</code>) to the allowed domains list.</p>
                  </div>
                </div>

                <div className="ef-embed-step">
                  <span className="ef-embed-step__num">4</span>
                  <div className="ef-embed-step__content">
                    <strong>Publish &amp; test</strong>
                    <p>Save and publish your website. The form will appear where you placed the script. Submissions show up in your dashboard.</p>
                  </div>
                </div>

                <div className="ef-embed-note">
                  <strong>Platform tips:</strong>
                </div>
                <ul className="ef-embed-platforms">
                  <li><strong>WordPress</strong> — "Custom HTML" block</li>
                  <li><strong>Wix</strong> — "Embed a Site" or "Custom Element"</li>
                  <li><strong>Shopify</strong> — Edit theme HTML</li>
                  <li><strong>Any page builder</strong> — "HTML" or "Code Embed" block</li>
                </ul>
              </div>
            ) : (
              /* ─── Floating Widget ───────────────────────────────────── */
              <div className="ef-embed-steps">
                <div className="ef-embed-note ef-embed-note--info">
                  A floating button appears on your website (like a chat widget). Visitors click it to open the enquiry form in a popup — <strong>no coding knowledge needed</strong>. Just copy and paste the script before <code>&lt;/body&gt;</code>.
                </div>

                <div className="ef-widget-config">
                  <div className="ef-widget-config__field">
                    <label>Button Position</label>
                    <div className="ef-widget-positions">
                      {[
                        { value: 'bottom-right', label: 'Bottom Right' },
                        { value: 'bottom-left', label: 'Bottom Left' },
                        { value: 'bottom-center', label: 'Bottom Center' },
                      ].map((pos) => (
                        <button
                          key={pos.value}
                          className={`ef-widget-pos-btn ${widgetConfig.position === pos.value ? 'ef-widget-pos-btn--active' : ''}`}
                          onClick={() => setWidgetConfig(c => ({ ...c, position: pos.value }))}
                        >
                          <span className={`ef-widget-pos-dot ef-widget-pos-dot--${pos.value}`} />
                          {pos.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="ef-widget-config__row">
                    <div className="ef-widget-config__field">
                      <label>Button Text</label>
                      <input
                        type="text"
                        value={widgetConfig.buttonText}
                        onChange={(e) => setWidgetConfig(c => ({ ...c, buttonText: e.target.value }))}
                        placeholder="Enquire Now"
                        maxLength={30}
                      />
                    </div>
                    <div className="ef-widget-config__field">
                      <label>Button Color</label>
                      <div className="ef-widget-color-pick">
                        <input
                          type="color"
                          value={widgetConfig.buttonColor}
                          onChange={(e) => setWidgetConfig(c => ({ ...c, buttonColor: e.target.value }))}
                        />
                        <span>{widgetConfig.buttonColor}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Live preview */}
                <div className="ef-widget-preview">
                  <label>Preview</label>
                  <div className="ef-widget-preview__area">
                    <div className="ef-widget-preview__page">
                      <div className="ef-widget-preview__header" />
                      <div className="ef-widget-preview__content">
                        <div className="ef-widget-preview__line" />
                        <div className="ef-widget-preview__line ef-widget-preview__line--short" />
                        <div className="ef-widget-preview__line" />
                      </div>
                      <div
                        className={`ef-widget-preview__btn ef-widget-preview__btn--${widgetConfig.position}`}
                        style={{ background: widgetConfig.buttonColor }}
                      >
                        {widgetConfig.buttonText}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="ef-embed-step">
                  <span className="ef-embed-step__num">1</span>
                  <div className="ef-embed-step__content">
                    <strong>Copy this script</strong>
                  </div>
                </div>

                <div className="ef-embed-code">
                  {getEmbedScript(embedForm, 'widget')}
                </div>

                <AppButton className="ef-embed-copy-btn" onClick={() => copyEmbedScript(embedForm, 'widget')}>
                  <Copy size={16} /> Copy Widget Script
                </AppButton>

                <div className="ef-embed-step">
                  <span className="ef-embed-step__num">2</span>
                  <div className="ef-embed-step__content">
                    <strong>Paste it before <code>&lt;/body&gt;</code> in your website</strong>
                    <p>The button will appear automatically. No need to place it in a specific location — it floats on the page.</p>
                  </div>
                </div>

                <div className="ef-embed-code ef-embed-code--example">
{`<!-- Paste before </body> -->
<body>
  ... your website content ...

  ${getEmbedScript(embedForm, 'widget')}
</body>`}
                </div>

                <div className="ef-embed-step">
                  <span className="ef-embed-step__num">3</span>
                  <div className="ef-embed-step__content">
                    <strong>Whitelist your domain</strong>
                    <p>Go to <strong>Settings → Public API</strong> and add your website's domain to the allowed list.</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default EnquiryFormsList;
